import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { CookieConsent } from '@/components/common/CookieConsent';
import { PWAInstallPrompt } from '@/components/common/PWAInstallPrompt';
import { useEffect, Suspense, lazy } from 'react';
import { reportError } from '@/lib/error-handling';

// Lazy load pages for code splitting
const Index = lazy(() => import('./pages/Index'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const AGB = lazy(() => import('./pages/AGB'));
const Auth = lazy(() => import('./pages/Auth'));
const AnfrageNeu = lazy(() => import('./pages/AnfrageNeu'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Admin = lazy(() => import('./pages/Admin'));
const FirmaRegister = lazy(() => import('./pages/FirmaRegister'));
const FirmaDashboard = lazy(() => import('./pages/FirmaDashboard'));
const PublicCompanyProfile = lazy(() => import('./pages/PublicCompanyProfile'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const UmzugBerlin = lazy(() => import('./pages/UmzugBerlin'));
const UmzugsfirmenBerlin = lazy(() => import('./pages/UmzugsfirmenBerlin'));
const UmzugHamburg = lazy(() => import('./pages/UmzugHamburg'));
const UmzugsfirmenHamburg = lazy(() => import('./pages/UmzugsfirmenHamburg'));
const UmzugMuenchen = lazy(() => import('./pages/UmzugMuenchen'));
const UmzugsfirmenMuenchen = lazy(() => import('./pages/UmzugsfirmenMuenchen'));
const UmzugKoeln = lazy(() => import('./pages/UmzugKoeln'));
const UmzugsfirmenKoeln = lazy(() => import('./pages/UmzugsfirmenKoeln'));
const UmzugFrankfurt = lazy(() => import('./pages/UmzugFrankfurt'));
const UmzugsfirmenFrankfurt = lazy(() => import('./pages/UmzugsfirmenFrankfurt'));
const UmzugStuttgart = lazy(() => import('./pages/UmzugStuttgart'));
const UmzugsfirmenStuttgart = lazy(() => import('./pages/UmzugsfirmenStuttgart'));
const Reinigung = lazy(() => import('./pages/Reinigung'));
const Entruempelung = lazy(() => import('./pages/Entruempelung'));
const ReinigungHamburg = lazy(() => import('./pages/ReinigungHamburg'));
const ReinigungHamburgFirmen = lazy(() => import('./pages/ReinigungHamburgFirmen'));
const ReinigungBerlin = lazy(() => import('./pages/ReinigungBerlin'));
const ReinigungBerlinFirmen = lazy(() => import('./pages/ReinigungBerlinFirmen'));
const ReinigungMuenchen = lazy(() => import('./pages/ReinigungMünchen'));
const ReinigungMuenchenFirmen = lazy(() => import('./pages/ReinigungMünchenFirmen'));
const ReinigungKoeln = lazy(() => import('./pages/ReinigungKöln'));
const ReinigungKoelnFirmen = lazy(() => import('./pages/ReinigungKölnFirmen'));
const ReinigungFrankfurtAmMain = lazy(() => import('./pages/ReinigungFrankfurtAmMain'));
const ReinigungFrankfurtAmMainFirmen = lazy(() => import('./pages/ReinigungFrankfurtAmMainFirmen'));
const ReinigungStuttgart = lazy(() => import('./pages/ReinigungStuttgart'));
const ReinigungStuttgartFirmen = lazy(() => import('./pages/ReinigungStuttgartFirmen'));
const EntruempelungHamburg = lazy(() => import('./pages/EntrümpelungHamburg'));
const EntruempelungHamburgEntruempelungsfirmen = lazy(
  () => import('./pages/EntrümpelungHamburgEntruempelungsfirmen')
);
const EntruempelungBerlin = lazy(() => import('./pages/EntrümpelungBerlin'));
const EntruempelungBerlinEntruempelungsfirmen = lazy(
  () => import('./pages/EntrümpelungBerlinEntruempelungsfirmen')
);
const EntruempelungMuenchen = lazy(() => import('./pages/EntrümpelungMünchen'));
const EntruempelungMuenchenEntruempelungsfirmen = lazy(
  () => import('./pages/EntrümpelungMünchenEntruempelungsfirmen')
);
const EntruempelungKoeln = lazy(() => import('./pages/EntrümpelungKöln'));
const EntruempelungKoelnEntruempelungsfirmen = lazy(
  () => import('./pages/EntrümpelungKölnEntruempelungsfirmen')
);
const EntruempelungFrankfurtAmMain = lazy(() => import('./pages/EntrümpelungFrankfurtAmMain'));
const EntruempelungFrankfurtAmMainEntruempelungsfirmen = lazy(
  () => import('./pages/EntrümpelungFrankfurtAmMainEntruempelungsfirmen')
);
const EntruempelungStuttgart = lazy(() => import('./pages/EntrümpelungStuttgart'));
const EntruempelungStuttgartEntruempelungsfirmen = lazy(
  () => import('./pages/EntrümpelungStuttgartEntruempelungsfirmen')
);
const CompanyCompare = lazy(() => import('./pages/CompanyCompare'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading component for suspense fallback
const PageLoader = () => {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center justify-center min-h-screen"
      role="status"
      aria-label={t('common.pageLoading')}
    >
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Ensure page scrolls to top when navigating to a new route
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LegacyHashRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;

    if (!hash.startsWith('#/')) {
      return;
    }

    const legacyTarget = hash.slice(1);
    const [legacyPath, legacyQuery] = legacyTarget.split('?');
    const nextPath = `${legacyPath}${legacyQuery ? `?${legacyQuery}` : ''}`;

    if (`${location.pathname}${location.search}` === nextPath) {
      window.history.replaceState(window.history.state, '', nextPath);
      return;
    }

    navigate(nextPath, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      const component =
        (query.meta?.errorContext as { component?: string } | undefined)?.component ??
        'QueryClient';
      reportError(`useQuery error (${query.queryHash})`, error, { component });
    },
  }),
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LegacyHashRedirect />
        <ScrollToTop />
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <CookieConsent />
            <PWAInstallPrompt />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/agb" element={<AGB />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/anfrage/neu" element={<AnfrageNeu />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/firma/register" element={<FirmaRegister />} />
              <Route path="/firma/dashboard" element={<FirmaDashboard />} />
              <Route path="/firma/:slug" element={<PublicCompanyProfile />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/umzug/berlin" element={<UmzugBerlin />} />
              <Route path="/umzugsfirmen/berlin" element={<UmzugsfirmenBerlin />} />
              <Route path="/umzug/hamburg" element={<UmzugHamburg />} />
              <Route path="/umzugsfirmen/hamburg" element={<UmzugsfirmenHamburg />} />
              <Route path="/umzug/muenchen" element={<UmzugMuenchen />} />
              <Route path="/umzugsfirmen/muenchen" element={<UmzugsfirmenMuenchen />} />
              <Route path="/umzug/koeln" element={<UmzugKoeln />} />
              <Route path="/umzugsfirmen/koeln" element={<UmzugsfirmenKoeln />} />
              <Route path="/umzug/frankfurt" element={<UmzugFrankfurt />} />
              <Route path="/umzugsfirmen/frankfurt" element={<UmzugsfirmenFrankfurt />} />
              <Route path="/umzug/stuttgart" element={<UmzugStuttgart />} />
              <Route path="/umzugsfirmen/stuttgart" element={<UmzugsfirmenStuttgart />} />
              <Route path="/reinigung" element={<Reinigung />} />
              <Route path="/reinigung/hamburg" element={<ReinigungHamburg />} />
              <Route path="/reinigung/firmen/hamburg" element={<ReinigungHamburgFirmen />} />
              <Route path="/reinigung/berlin" element={<ReinigungBerlin />} />
              <Route path="/reinigung/firmen/berlin" element={<ReinigungBerlinFirmen />} />
              <Route path="/reinigung/muenchen" element={<ReinigungMuenchen />} />
              <Route path="/reinigung/firmen/muenchen" element={<ReinigungMuenchenFirmen />} />
              <Route path="/reinigung/koeln" element={<ReinigungKoeln />} />
              <Route path="/reinigung/firmen/koeln" element={<ReinigungKoelnFirmen />} />
              <Route path="/reinigung/frankfurt" element={<ReinigungFrankfurtAmMain />} />
              <Route
                path="/reinigung/firmen/frankfurt"
                element={<ReinigungFrankfurtAmMainFirmen />}
              />
              <Route path="/reinigung/stuttgart" element={<ReinigungStuttgart />} />
              <Route path="/reinigung/firmen/stuttgart" element={<ReinigungStuttgartFirmen />} />
              <Route path="/entsorgung" element={<Entruempelung />} />
              <Route path="/entsorgung/hamburg" element={<EntruempelungHamburg />} />
              <Route
                path="/entsorgung/firmen/hamburg"
                element={<EntruempelungHamburgEntruempelungsfirmen />}
              />
              <Route path="/entsorgung/berlin" element={<EntruempelungBerlin />} />
              <Route
                path="/entsorgung/firmen/berlin"
                element={<EntruempelungBerlinEntruempelungsfirmen />}
              />
              <Route path="/entsorgung/muenchen" element={<EntruempelungMuenchen />} />
              <Route
                path="/entsorgung/firmen/muenchen"
                element={<EntruempelungMuenchenEntruempelungsfirmen />}
              />
              <Route path="/entsorgung/koeln" element={<EntruempelungKoeln />} />
              <Route
                path="/entsorgung/firmen/koeln"
                element={<EntruempelungKoelnEntruempelungsfirmen />}
              />
              <Route path="/entsorgung/frankfurt" element={<EntruempelungFrankfurtAmMain />} />
              <Route
                path="/entsorgung/firmen/frankfurt"
                element={<EntruempelungFrankfurtAmMainEntruempelungsfirmen />}
              />
              <Route path="/entsorgung/stuttgart" element={<EntruempelungStuttgart />} />
              <Route
                path="/entsorgung/firmen/stuttgart"
                element={<EntruempelungStuttgartEntruempelungsfirmen />}
              />
              <Route path="/vergleich" element={<CompanyCompare />} />
              <Route path="/firmen-vergleich" element={<CompanyCompare />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
